/**
 * DEK (マスターキー) 管理者向け説明文
 * セキュリティ画面の「DEKの操作方法はこちら」から表示するモーダル用
 */

export const DEK_INSTRUCTIONS_JA = `
## 1. マスターキー（DEK）とは

- **DEK（Data Encryption Key）** は、本社の給与・賞与の金額データをデータベースで保護するための「マスターキー」です。
- **レベル1管理者**（管理者）のみが設定・リセット・管理できます。
- システム稼働中にDEKが利用できない場合（例：再起動後）、給与・賞与の金額は復号されず、**ゼロとして表示**されます。

---

## 2. 初回設定

- メニューから **「セキュリティ」** を開き、管理者パスワードを入力してください。
- まだDEKが設定されていない場合は、**初回設定**フォームで以下を入力します。
  - 管理者パスワード
  - **新しいDEK**（12文字、3つに分けて入力。大文字・小文字・数字・記号をそれぞれ1文字以上含めてください）
- **DEKを設定したら、必ず安全な場所**（金庫や鍵付きキャビネットなど）**に保管してください。** リセット時やアクセス回復時に必要になります。
- **DEKを紛失した場合：** そのDEKで暗号化された既存の給与・賞与データは**復号できません**。システム上でその金額を閲覧することはできなくなります。暗号化を再度利用するには、セキュリティ画面で**新しいDEK**を設定する必要があります。新しいデータは新しいキーで暗号化されます。紛失したDEKで暗号化されたデータを復元する方法はないため、DEKの安全なバックアップ保管が重要です。
- 設定後は、これまでどおり給与・賞与データのアップロードや利用ができ、金額データは暗号化して保存されます。

---

## 3. マスターキーのリセット

- **「セキュリティ」** → **「マスターキーをリセットする」** を開きます。
- 以下を入力してください。
  - **現在の管理者パスワード**
  - **現在のDEK**（現在使用中のキー）
  - **新しいDEK**（2回入力）
- リセット成功後は、画面の手順に従って操作してください。
  - **管理者：** 1回ログアウトし、**現在の**（変更していない）パスワードで再度ログインしてください。
  - **本社従業員：** 各自、**現在の**パスワードで**1回**ログインしてください。パスワードの変更は不要です。その後、給与データをこれまでどおり閲覧できます。

---

## 4. 管理者が自分のパスワードを変更する場合

- **推奨：** **「設定」** → **「パスワードを変更」** を使用してください。
  - **現在のパスワード** と **新しいパスワード** を入力します。
  - システムがDEKを新しいパスワードで再暗号化するため、再起動後も給与データは正常に表示されます。
- **従業員管理** で自分のアカウントを編集してパスワードを変更する場合：
  - 新しいパスワードを設定するときは、編集画面で**必ず「現在のパスワード」を入力**してください。
  - 入力しないと、DEKが新しいパスワードで使えなくなり、再起動後に金額がゼロ表示になるか、DEKを再度設定するまで閲覧できません。

---

## 5. 他の従業員のパスワードを変更する場合（従業員管理 → 編集）

- その従業員の「現在のパスワード」は入力しなくても、新しいパスワードを設定できます。
- 保存後、その従業員のDEKとの紐づけはいったん解除され、**次回ログイン時**に再設定されます。
- **サーバーやコンテナの再起動後：**
  - まず**管理者**がログインし、DEKをメモリに読み込ませてください。
  - その後、その従業員が**新しい**パスワードで1回ログインすれば、給与データを通常どおり閲覧できます。

---

## 6. サーバーやコンテナの再起動後

- **再起動が行われた場合は、開発者から連絡があります。**
- DEKは、誰かがログインして読み込むまでメモリに保持されません。
- **再起動のたびに、管理者が少なくとも1回はログインする**（または「セキュリティ」を開いてパスワードを入力する）必要があります。
- それまで、給与・賞与の金額は全員分ゼロ表示になる場合があります。
- 従業員は再起動後にパスワードを変更する必要はありません。管理者がログインしたあと、各自1回ログインすれば十分です。

---

## 7. 「セキュリティ」を開ける人

- **レベル1管理者**（管理者）アカウントのみが「セキュリティ」を表示・開けます。
- 現場管理者や一般ユーザーは、DEKの設定やリセットにはアクセスできません。

---

## 8. 注意事項

- **セクション3の手順**（管理者の再ログイン、従業員の1回ログイン）を理解し、実行できる場合以外は、DEKをリセットしないでください。

---

## 9. クイックリファレンス

| 状況 | 対応 |
|------|------|
| 初めてDEKを使う | セキュリティ → DEKを設定（パスワード＋12文字キー）。**DEKは安全な場所に保管。** |
| 自分のパスワードを変更 | 設定 → パスワードを変更を推奨。従業員管理で編集する場合は必ず「現在のパスワード」を入力。 |
| 他の従業員のパスワードを変更 | 従業員管理 → 編集 → 新しいパスワードを設定。再起動後は管理者が先にログインし、その後その従業員が1回ログイン。 |
| DEKリセット後 | 管理者：1回ログアウトしてログイン。従業員：現在のパスワードで1回ログイン。 |
| サーバー・コンテナ再起動後 | 管理者が1回ログイン（またはセキュリティを開いてパスワード入力）。その後は通常どおり利用可能。 |
| DEKを紛失した場合 | 既存の暗号化済み給与・賞与データは復元できません。セキュリティで新しいDEKを設定し、今後のデータ用に使用。新しいDEKも安全に保管してください。 |
`;
